import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import SisCorrectionReview from "./SisCorrectionReview";
import SisVarianceDecision from "./SisVarianceDecision";
import SisInvoiceReplacement from "./SisInvoiceReplacement";
import SisSupplierCreditNotes from './SisSupplierCreditNotes';
import SisRemainingCorrection from './SisRemainingCorrection';
import SisGrossInvoiceCredits from './SisGrossInvoiceCredits';
import SisMultiBillCorrections from './SisMultiBillCorrections';
const control = "mt-1 w-full rounded-lg border p-3",
  button = "rounded-xl bg-primary-700 px-4 py-3 text-white disabled:opacity-40";
const Field = ({ label, children }) => (
  <label className="block text-sm">
    {label}
    {children}
  </label>
);
export default function SisLifecyclePanel({
  company,
  saleId,
  record,
  workspace,
  onCancelled,
}) {
  const { accessToken, user } = useAuth(),
    postable = hasPermission(user, "accounting.vouchers_post"),
    returnable = postable && hasPermission(user, "pos.return");
  const [mode, setMode] = useState("invoice"),
    [form, setForm] = useState({}),
    [history, setHistory] = useState([]),
    [matches, setMatches] = useState([]),
    [varianceReviews,setVarianceReviews]=useState([]),
    [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [extraBills, setExtraBills] = useState([]),
    [available, setAvailable] = useState([]),
    [nextCursor, setNextCursor] = useState(null),
    [billNotice, setBillNotice] = useState("");
  const epoch = useRef(0),
    key = useRef(null),
    inFlight = useRef(false),
    base = `/accounting/${company}/supplier-liabilities/sis`;
  function reset() {
    epoch.current++;
    key.current = null;
    setPreview(null);
    setChecks({});
    setError("");
    setNotice("");
  }
  useEffect(() => {
    let active = true;
    reset();
    setForm({});
    setHistory([]);
    setMatches([]);
    setVarianceReviews([]);
    setExtraBills([]);
    setAvailable([]);
    setNextCursor(null);
    setBillNotice("");
    setBusy(false);
    inFlight.current = false;
    Promise.all([
      apiRequest(`${base}/pos/${saleId}/returns`, { accessToken }),
      apiRequest(`${base}/invoices`, { accessToken }),
      apiRequest(`${base}/invoices/variance-reviews`,{accessToken}),
    ])
      .then(([h, m, v]) => {
        if (active) {
          setHistory(h);
          setVarianceReviews(v.filter(x=>x.snapshot.allocations.some(a=>a.accrualId===record.id)));
          setMatches(
            m.filter((x) =>
              x.snapshot.allocations.some((a) => a.accrualId === record.id),
            ),
          );
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
      epoch.current++;
    };
  }, [base, saleId, accessToken, record.id]);
  function edit(name, value) {
    reset();
    if (name === "billNumber") {
      setExtraBills([]);
      setAvailable([]);
      setNextCursor(null);
      setBillNotice("");
    }
    setForm((f) => ({ ...f, [name]: value }));
  }
  async function run(fn, apply) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const ticket = epoch.current;
    try {
      const r = await fn();
      if (ticket === epoch.current) apply(r);
    } catch (e) {
      if (ticket === epoch.current) setError(e.message);
    } finally {
      if (ticket === epoch.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  const bills = record.snapshot.bills,
    origin = record.snapshot.source.lines,
    chosen = bills.find((b) => b.number === form.billNumber),
    endpoint =
      mode === "invoice" ? `${base}/invoices` : `${base}/pos/${saleId}/returns`;
  function loadBills(after) {
    if (!chosen) return;
    run(
      () =>
        apiRequest(
          `${base}/invoices/available-bills?supplierId=${encodeURIComponent(chosen.supplierId)}&registrationId=${encodeURIComponent(record.snapshot.registrationId)}${after ? `&after=${encodeURIComponent(after)}` : ""}`,
          { accessToken },
        ),
      (r) => {
        setAvailable((old) => (after ? [...old, ...r.bills] : r.bills));
        setNextCursor(r.nextCursor);
        setBillNotice(r.notice);
      },
    );
  }
  function addBill(b) {
    reset();
    setExtraBills((old) => [...old, { ...b, amount: "" }]);
  }
  function extraAmount(index, value) {
    reset();
    setExtraBills((old) =>
      old.map((b, i) => (i === index ? { ...b, amount: value } : b)),
    );
  }
  function payload() {
    if (mode === "invoice")
      return {
        supplierId: chosen?.supplierId,
        registrationId: record.snapshot.registrationId,
        invoiceNumber: form.invoiceNumber || "",
        invoiceDate: form.invoiceDate || "",
        supplierGstin: form.supplierGstin || "",
        taxableAmount: form.taxableAmount || "0",
        cgst: form.cgst || "0",
        sgst: form.sgst || "0",
        igst: form.igst || "0",
        evidenceReference: form.evidenceReference || "",
        reviewNote: form.reviewNote || "",
        allocations: [
          {
            accrualId: record.id,
            billNumber: form.billNumber,
            amount: form.amount || "0",
          },
          ...extraBills.map((b) => ({
            accrualId: b.accrualId,
            billNumber: b.billNumber,
            amount: b.amount || "0",
          })),
        ],
      };
    return {
      date: form.date || "",
      refundLiabilityLedgerId: form.refundLiabilityLedgerId,
      reason: form.reason || "",
      customerCreditNote: form.customerCreditNote || "",
      supplierCreditReference: form.supplierCreditReference || "",
      items: origin
        .filter((l) => form[l.saleItemId])
        .map((l) => ({
          saleItemId: l.saleItemId,
          quantity: form[l.saleItemId],
        })),
    };
  }
  function review(e,variance=false) {
    e.preventDefault();
    reset();
    run(
      () =>
        apiRequest((variance?`${base}/invoices/variance-reviews`:endpoint) + "/preview", {
          method: "POST",
          accessToken,
          body: payload(),
        }),
      setPreview,
    );
  }
  function execute() {
    const variance=preview?.mode==='UNRESOLVED_INVOICE_VARIANCE_REVIEW';
    run(
      async () => {
        if (!window.crypto?.randomUUID)
          throw Error("Secure UUID support required");
        if (!key.current) key.current = window.crypto.randomUUID();
        return apiRequest(variance?`${base}/invoices/variance-reviews`:endpoint, {
          method: "POST",
          accessToken,
          body: {
            ...payload(),
            previewHash: preview.previewHash,
            requestKey: key.current,
            ...(mode === "invoice"
              ? {
                  actualInvoiceReviewed: checks.a === true,
                  noDuplicateEntryConfirmed: checks.b === true,
                  noItcClaimConfirmed: checks.c === true,
                }
              : {
                  goodsReceivedConfirmed: checks.a === true,
                  creditNoteTaxReviewed: checks.b === true,
                  supplierCreditAccepted: checks.c === true,
                  refundNotPaidConfirmed: checks.d === true,
                }),
          },
        });
      },
      (r) => {
        setPreview(null);
        setNotice(
          variance ? 'Unresolved variance evidence retained. No invoice match, supplier adjustment or input GST claim was created.' : mode === "invoice"
            ? "Actual invoice matched. No expense, payable or input GST was posted."
            : "Return posted. Physical stock and supplier credit recorded; customer refund is still unpaid. Do not process this return again in ordinary POS.",
        );
        if (mode === "return") setHistory((h) => [...h, r]);
        else if(variance){setVarianceReviews(h=>[r,...h.filter(x=>x.id!==r.id)]);}
        else {
          setMatches((h) => [...h, r]);
          setExtraBills([]);
          setAvailable([]);
          setNextCursor(null);
          setBillNotice("");
        }
      },
    );
  }
  const labels =
    preview?.mode==='UNRESOLVED_INVOICE_VARIANCE_REVIEW' ? ['I checked the actual invoice and evidence reference.','This retains an unresolved difference only; it does not approve or post it.','No input GST claim is authorized by this review.'] : mode === "invoice"
      ? [
          "I checked the actual supplier tax invoice and its document reference.",
          "I checked prior entries; this match must not create duplicate cost or payable.",
          "I understand no input GST credit is claimed by this match.",
        ]
      : [
          "I confirm these goods were physically received against the original sale.",
          "I reviewed the actual customer credit note and permitted output-tax adjustment.",
          "I have the supplier’s acceptance/reference for this commercial credit.",
          "No customer refund has been paid; record the amount as refund liability.",
        ];
  if (record.cancellation)
    return (
      <section className="space-y-4 rounded-2xl border border-amber-200 bg-white p-6">
        <h2 className="text-xl font-semibold">
          SIS accrual cancelled original history preserved
        </h2>
        <p>
          Cancellation voucher{" "}
          {record.cancellation.snapshot?.reversalNumber ||
            record.cancellation.voucherId}
          . No stock, payment, refund or replacement accrual was created. This
          source stays frozen; invoice matching, sales posting and physical
          return actions are unavailable for the cancelled accrual.
        </p>
        <SisCorrectionReview
          company={company}
          saleId={saleId}
          accessToken={accessToken}
          canRequest={false}
          canExecute={false}
        />
      </section>
    );
  return (
    <section className="space-y-5 rounded-2xl bg-white p-6">
      <h2 className="text-xl font-semibold">
        SIS sales, invoices and corrections
      </h2>
      <p className="text-sm">
        CHARMS is the invoice seller. Review and post its original sales/GST
        under{" "}
        <a
          className="underline"
          href="/admindashboard/accounting/sales-snapshots"
        >
          Sales Reviews
        </a>
        ; SIS cost and supplier payable are not posted again. For a wrong
        receipt, use a reviewed full return/credit and issue a new corrected
        receipt; do not overwrite history.
      </p>
      <p className="rounded-lg bg-amber-50 p-3 text-sm">
        Returns create an unpaid customer credit, not a refund payment. Ordinary
        source edits stay locked. Points, coupons, commissions, receipt rounding
        and unsupported stock valuations remain blocked. Reconcile and pay
        accepted supplier/customer credits separately in Accounting. Invoice
        matching is documentary reconciliation, not an ITC claim or statutory
        filing.
      </p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      <SisRemainingCorrection key={`${company}:${saleId}`} base={base} saleId={saleId} accessToken={accessToken} allowed={postable} blocked={busy} onPosted={r=>{reset();setAvailable([]);setExtraBills([]);setNextCursor(null);setMatches(rows=>rows.map(x=>x.snapshot.allocations?.length===1&&x.snapshot.allocations[0].accrualId===r.accrualId?{...x,remainingCorrection:{id:r.id,accrualId:r.accrualId,previewHash:r.previewHash}}:x));setNotice('Remaining supplier balance corrected. Prior history preserved; refresh available bills and supplier outstanding separately.');}}/>
      <SisGrossInvoiceCredits key={`gross:${company}:${record.id}`} base={base} record={record} invoices={matches} accessToken={accessToken} allowed={postable} canReverse={hasPermission(user,'accounting.vouchers_reverse')} blocked={busy} onSaved={async()=>{reset();setAvailable([]);setExtraBills([]);const rows=await apiRequest(`${base}/invoices`,{accessToken});setMatches(rows.filter(x=>x.snapshot.allocations.some(a=>a.accrualId===record.id)));}}/>
      {notice && (
        <p role="status" className="text-green-800">
          {notice}
        </p>
      )}
      <SisCorrectionReview
        company={company}
        saleId={saleId}
        accessToken={accessToken}
        ledgers={workspace?.ledgers || []}
        canRequest={hasPermission(user, "accounting.vouchers_reverse")}
        canExecute={
          postable && hasPermission(user, "accounting.vouchers_reverse")
        }
        onCancelled={onCancelled}
      />
      <div className="flex gap-3">
        <button
          type="button"
          disabled={busy}
          className={button}
          onClick={() => {
            reset();
            setForm({});
            setExtraBills([]);
            setAvailable([]);
            setNextCursor(null);
            setBillNotice("");
            setMode("invoice");
          }}
        >
          Match supplier invoice
        </button>
        <button
          type="button"
          disabled={busy}
          className={button}
          onClick={() => {
            reset();
            setForm({});
            setExtraBills([]);
            setAvailable([]);
            setNextCursor(null);
            setBillNotice("");
            setMode("return");
          }}
        >
          Customer return / credit-and-reissue
        </button>
      </div>
      <form onSubmit={review} className="grid gap-4 md:grid-cols-2">
        <fieldset disabled={busy} className="contents">
          {mode === "invoice" ? (
            <>
              <Field label="Original SIS supplier bill">
                <select
                  required
                  className={control}
                  value={form.billNumber || ""}
                  onChange={(e) => edit("billNumber", e.target.value)}
                >
                  <option value="">Choose bill</option>
                  {bills.map((b) => (
                    <option key={b.number} value={b.number}>
                      {b.supplierName} · {b.number} · ₹{b.amount}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="space-y-3 rounded-xl border p-4 md:col-span-2">
                <h3 className="font-semibold">
                  Match one invoice across multiple accrued bills
                </h3>
                <p className="text-sm">
                  Select the original bill above to establish the supplier and
                  GST registration. Add explicitly reviewed amounts from other
                  bills under that same scope. Remaining unmatched amounts are
                  not unpaid balances; this does not post another payable or
                  claim input GST.
                </p>
                <button
                  type="button"
                  disabled={busy || !chosen}
                  className={button}
                  onClick={() => loadBills(null)}
                >
                  Load same-supplier bills
                </button>
                {billNotice && <p className="text-sm">{billNotice}</p>}
                {available.some(b=>b.reviewRequired)&&<div role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"><p className="font-semibold">Invoice / return documentary review needed on this loaded page</p>{available.filter(b=>b.reviewRequired).map(b=><p key={b.accrualId+':'+b.billNumber}>{b.receiptNumber} · {b.billNumber}: available to match ₹{b.availableToMatch}; documentary difference ₹{b.documentaryExcess}. Review actual supplier invoice/credit-note evidence. This is not unpaid supplier balance.</p>)}</div>}
                {available
                  .filter(
                    (b) =>
                      !(
                        b.accrualId === record.id &&
                        b.billNumber === form.billNumber
                      ) &&
                      !extraBills.some(
                        (x) =>
                          x.accrualId === b.accrualId &&
                          x.billNumber === b.billNumber,
                      ),
                  )
                  .map((b) => (
                    <div
                      key={b.accrualId + ":" + b.billNumber}
                      className="flex flex-wrap items-center gap-3 border-t pt-2 text-sm"
                    >
                      <span>
                        {b.receiptNumber} · {b.billNumber} · Original ₹
                        {b.original} · Matched ₹{b.matched} · Returned credit ₹
                        {b.credited} · Available to match ₹{b.availableToMatch??b.remaining}
                        {b.reviewRequired&&<strong className="ml-2 text-amber-800">Documentary difference ₹{b.documentaryExcess} — review supplier credit-note/invoice evidence; not a payable balance.</strong>}
                        {b.correctionNotice&&<strong className="block text-amber-800">{b.correctionNotice}</strong>}
                      </span>
                      <button
                        type="button"
                        disabled={
                          busy ||
                          b.documentaryMatchingBlocked ||
                          !/^\d+(\.\d{1,2})?$/.test(b.remaining) ||
                          Number(b.remaining) <= 0 ||
                          extraBills.length >= 99
                        }
                        className="rounded border px-3 py-2 disabled:opacity-40"
                        onClick={() => addBill(b)}
                      >
                        Add {b.billNumber}
                      </button>
                    </div>
                  ))}
                {nextCursor && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => loadBills(nextCursor)}
                    className="rounded border px-3 py-2"
                  >
                    Load more accrued bills
                  </button>
                )}
                {extraBills.map((b, i) => (
                  <div
                    key={b.accrualId + ":" + b.billNumber}
                    className="space-y-2 border-t pt-3"
                  >
                    <Field label={`Gross allocation for ${b.billNumber}`}>
                      <input
                        required
                        inputMode="decimal"
                        pattern="[0-9]+([.][0-9]{1,2})?"
                        className={control}
                        value={b.amount}
                        onChange={(e) => extraAmount(i, e.target.value)}
                      />
                    </Field>
                    <p className="text-sm">
                      Receipt {b.receiptNumber} · Last observed unmatched ₹
                      {b.remaining}. Server preview checks the current balance
                      again.
                    </p>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        reset();
                        setExtraBills((old) => old.filter((_, j) => j !== i));
                      }}
                      className="text-sm underline"
                    >
                      Remove {b.billNumber}
                    </button>
                  </div>
                ))}
                <p className="text-sm">
                  Selected bill allocations: {1 + extraBills.length}. Their
                  exact sum must equal the invoice taxable amount plus actual
                  CGST, SGST and IGST for exact matching. If they differ, use
                  the separate variance-review action; it does not match or
                  consume these amounts. No automatic pro-rata allocation.
                </p>
              </div>
              {[
                "invoiceNumber",
                "invoiceDate",
                "supplierGstin",
                "taxableAmount",
                "cgst",
                "sgst",
                "igst",
                "amount",
                "evidenceReference",
                "reviewNote",
              ].map((name) => (
                <Field
                  key={name}
                  label={
                    {
                      invoiceNumber: "Actual supplier invoice number",
                      invoiceDate: "Supplier invoice date",
                      supplierGstin: "Supplier GSTIN",
                      taxableAmount: "Actual taxable amount",
                      cgst: "Actual CGST",
                      sgst: "Actual SGST",
                      igst: "Actual IGST",
                      amount: "Gross amount matched to this bill",
                      evidenceReference: "Actual invoice evidence reference",
                      reviewNote: "Invoice and prior-entry review note",
                    }[name]
                  }
                >
                  <input
                    required={!["cgst", "sgst", "igst"].includes(name)}
                    minLength={name === "reviewNote" ? 20 : undefined}
                    type={name === "invoiceDate" ? "date" : "text"}
                    className={control}
                    value={form[name] || ""}
                    onChange={(e) => edit(name, e.target.value)}
                  />
                </Field>
              ))}
            </>
          ) : (
            <>
              <Field label="Return date (today in India)">
                <input
                  required
                  type="date"
                  className={control}
                  value={form.date || ""}
                  onChange={(e) => edit("date", e.target.value)}
                />
              </Field>
              <Field label="Dedicated customer-refund liability ledger">
                <select
                  required
                  className={control}
                  value={form.refundLiabilityLedgerId || ""}
                  onChange={(e) =>
                    edit("refundLiabilityLedgerId", e.target.value)
                  }
                >
                  <option value="">Choose reviewed refund liability</option>
                  {(workspace.ledgers || [])
                    .filter(
                      (l) =>
                        l.kind === "GENERAL" &&
                        l.group?.nature === "LIABILITY" &&
                        !l.party,
                    )
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.code} · {l.name}
                      </option>
                    ))}
                </select>
              </Field>
              {["customerCreditNote", "supplierCreditReference", "reason"].map(
                (name) => (
                  <Field
                    key={name}
                    label={
                      name === "reason"
                        ? "Reason / corrected receipt reference"
                        : name === "customerCreditNote"
                          ? "Actual customer credit-note reference"
                          : "Accepted supplier credit reference"
                    }
                  >
                    <input
                      required
                      minLength={name === "reason" ? 20 : 3}
                      className={control}
                      value={form[name] || ""}
                      onChange={(e) => edit(name, e.target.value)}
                    />
                  </Field>
                ),
              )}
              {origin.map((l) => (
                <Field
                  key={l.saleItemId}
                  label={`Return quantity for original item ${l.saleItemId} (sold ${l.quantity})`}
                >
                  <input
                    inputMode="decimal"
                    placeholder="Leave blank if not returned"
                    className={control}
                    value={form[l.saleItemId] || ""}
                    onChange={(e) => edit(l.saleItemId, e.target.value)}
                  />
                </Field>
              ))}
            </>
          )}
          <button className={button} disabled={busy || !postable}>
            Preview{" "}
            {mode === "invoice" ? "invoice match" : "coordinated return"}
          </button>
          {mode==='invoice'&&<button type="button" className="ml-3 rounded-xl border border-primary-200 bg-white px-4 py-3 text-primary-800 disabled:opacity-40" disabled={busy||!postable} onClick={e=>review(e,true)}>Review invoice variance instead</button>}
        </fieldset>
      </form>
      {preview && (
        <div className="space-y-4 rounded-xl border p-4">
          <p>{preview.warning}</p>
          <p className="font-semibold">
            {mode === "invoice"
              ? `Invoice gross ₹${preview.snapshot.total}`
              : `Customer credit ₹${preview.payload.totals.refund} · Supplier credit ₹${preview.payload.totals.supplierCredit}`}
          </p>
          {preview.mode==='UNRESOLVED_INVOICE_VARIANCE_REVIEW'&&<p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Accrued amounts selected ₹{preview.snapshot.accruedGross} · Signed invoice difference ₹{preview.snapshot.grossVariance} · {preview.snapshot.varianceDirection}. Unresolved evidence only; no posting or input GST claim.</p>}
          {mode!=='invoice'&&preview.payload.duesRectificationId&&<p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">Supplier credit uses reviewed corrected product dues, not the original agreement amount. Rectification {preview.payload.duesRectificationId} · Product allocation {preview.payload.rectificationBasisId}. Cumulative partial-return rounding is applied; the customer refund remains unpaid.</p>}
          {mode === "invoice" && preview.snapshot.allocations && (
            <ul className="space-y-1 text-sm">
              {preview.snapshot.allocations.map((a) => (
                <li key={a.accrualId + ":" + a.billNumber}>
                  {a.billNumber} · ₹{a.amount} · accrual {a.accrualId}
                </li>
              ))}
            </ul>
          )}
          {labels.map((label, i) => (
            <label key={label} className="flex gap-3 text-sm">
              <input
                type="checkbox"
                disabled={busy}
                checked={!!checks["abcd"[i]]}
                onChange={(e) =>
                  setChecks({ ...checks, ["abcd"[i]]: e.target.checked })
                }
              />
              {label}
            </label>
          ))}
          <button
            type="button"
            className={button}
            disabled={
              busy ||
              !(mode === "invoice" ? postable : returnable) ||
              labels.some((_, i) => !checks["abcd"[i]])
            }
            onClick={execute}
          >
            {preview.mode==='UNRESOLVED_INVOICE_VARIANCE_REVIEW'?'Retain unresolved variance review':mode === "invoice"
              ? "Retain invoice match"
              : "Post physical return and unpaid credit"}
          </button>
        </div>
      )}
      {!!varianceReviews.length&&<section className="space-y-2 rounded-xl border border-amber-200 p-4"><h3 className="font-semibold">Retained invoice variance reviews</h3><p className="text-sm text-gray-500">Review and acceptance alone do not post books. Reversed adjustments and invoice reconciliations remain visible as history.</p>{varianceReviews.map(v=><article key={v.id} className="border-b pb-3"><p className="text-sm">{v.snapshot.invoiceNumber} · Invoice ₹{v.snapshot.total} · Selected accrual ₹{v.snapshot.accruedGross} · Difference ₹{v.snapshot.grossVariance} · {v.status || 'UNRESOLVED'} / no ITC claim</p><SisVarianceDecision key={`${company}:${v.id}`} review={v} base={base} accessToken={accessToken} allowed={postable} canReverse={hasPermission(user,'accounting.vouchers_reverse')} onSaved={r=>setVarianceReviews(rows=>rows.map(x=>x.id===v.id?{...x,status:r.detail.status,decision:{id:r.id,createdAt:r.createdAt,...r.detail}}:x))} onPosted={r=>setVarianceReviews(rows=>rows.map(x=>x.id===v.id?{...x,adjustment:r,status:r.reversal?'RECTIFICATION_REVERSED':'FINANCIALLY_POSTED_NO_ITC'}:x))} /></article>)}</section>}
      {!!history.length && (
        <div>
          <h3 className="font-semibold">
            Retained customer credits — original amounts, not current unpaid
            balances
          </h3>
          {history.map((h) => (
            <p key={h.id} className="mt-2 text-sm">
              {h.snapshot.date} · {h.snapshot.customerCreditNote} · Customer ₹
              {h.snapshot.totals.refund} · Supplier ₹
              {h.snapshot.totals.supplierCredit}
              {h.snapshot.rectificationBasisId&&<> · Corrected product allocation {h.snapshot.rectificationBasisId}</>}
            </p>
          ))}
        </div>
      )}
      <SisMultiBillCorrections key={`grouped:${company}:${record.id}:${accessToken}`} base={base} record={record} accessToken={accessToken} allowed={postable} blocked={busy} onPosted={()=>{reset();setAvailable([]);setExtraBills([]);setBillNotice('Grouped correction posted. Reload invoice availability before further review.');}}/>
      <SisSupplierCreditNotes key={`${company}:${record.id}`} base={base} registrationId={record.snapshot.registrationId} bills={record.snapshot.bills} accessToken={accessToken} allowed={postable} canRelease={postable&&hasPermission(user,'accounting.vouchers_reverse')}/>
      {!!matches.length && (
        <div>
          <h3 className="font-semibold">
            Retained supplier invoice matches — no input GST claimed
          </h3>
          {matches.map((h) => (
            <article key={`${company}:${h.id}`} className="mt-2 text-sm">
              <p>
              {h.invoiceNumber} · {h.snapshot.invoiceDate} · ₹{h.snapshot.total}{" "}
              {h.snapshot.creditMatches?.length>0&&<span> · Credit evidence ₹{h.snapshot.creditOffsetAmount} · Net documentary allocation ₹{h.snapshot.netAmount}</span>}
              {h.documentaryRelease&&<strong className="ml-2 text-amber-800">Documentary reconciliation released — historical evidence only</strong>}
              · {h.snapshot.evidenceReference}
              {' · Version '}{h.revision||1}{h.replaces&&' · corrected replacement'}
              {h.reversal && <span className="ml-2 font-semibold text-amber-800">Reconciliation reversed — historical evidence only</span>}
              </p>
              <SisInvoiceReplacement key={`${h.id}:${h.remainingCorrection?.id||'ordinary'}`} invoice={h} base={base} accessToken={accessToken} allowed={postable&&hasPermission(user,'accounting.vouchers_reverse')} onReplaced={r=>setMatches(rows=>rows.map(x=>x.id===h.id?{...x,supersededBy:r}:x))}/>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
